import { Fragment, type ReactNode } from 'react';

/**
 * About-page markdown: paragraphs, `##` headings, and [label](url) links.
 * Text is React children, so tags in the file show up as text. A link is only
 * an anchor when the URL is http(s) or mailto.
 */
const LINK = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;

function safeHref(url: string): string | null {
  if (/^mailto:[^\s<>"']+$/i.test(url)) return url;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') return url;
  } catch {
    // Not a URL: the label is shown as text.
  }
  return null;
}

function inline(text: string, key: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK)) {
    const index = match.index ?? 0;
    if (index > last) nodes.push(<Fragment key={`${key}-t-${last}`}>{text.slice(last, index)}</Fragment>);
    const label = match[1] ?? '';
    const href = safeHref(match[2] ?? '');
    nodes.push(
      href ? (
        <a key={`${key}-a-${index}`} href={href}>
          {label}
        </a>
      ) : (
        <Fragment key={`${key}-a-${index}`}>{label}</Fragment>
      ),
    );
    last = index + match[0].length;
  }
  if (last < text.length) nodes.push(<Fragment key={`${key}-t-${last}`}>{text.slice(last)}</Fragment>);
  return nodes;
}

/** Blank lines separate blocks. A block that is one `##` line is a heading. */
export function AboutMarkdown({ source }: { source: string }) {
  const blocks = source.replace(/\r\n/g, '\n').trim().split(/\n{2,}/);
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);
        const text = lines.join(' ');
        if (!text) return null;
        const heading = lines.length === 1 ? /^##\s+(.+)$/.exec(lines[0]!) : null;
        if (heading) return <h2 key={i}>{heading[1]}</h2>;
        return <p key={i}>{inline(text, String(i))}</p>;
      })}
    </>
  );
}
